import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import './App.css'
import './css/ds-vitrine.css'
import './css/ds-app.css'
import './css/ds-scope-landing.css'
import './css/ds-compte.css'
import './css/ds-quiz.css'
import './css/ds-admin.css'
import './css/ds-editors.css'
import './css/ds-custom.css'
import './css/ds-arcnav.css'
import './css/ds-renderers.css'
import './css/ds-livre.css'
import './css/ds-welcome.css'
import './css/ds-fx.css'

import { useEffect, lazy, Suspense } from 'react'
import Header from './components/layout/Header'
import useAuthStore from './store/authStore'
import CookieConsent from './components/ui/CookieConsent'
import ScrollToTop from './components/ui/ScrollToTop'
import AdminGateHost from './components/admin/AdminGateHost'
import Home from './pages/public/Home'
import Login from './pages/public/Login'
import Register from './pages/public/Register'
import MentionsLegales from './pages/public/MentionsLegales'
import CGV from './pages/public/CGV'
import Confidentialite from './pages/public/Confidentialite'
import ResetPassword from './pages/public/ResetPassword'
import MaThematiqueLanding from './pages/public/MaThematiqueLanding'
import QuizCode from './pages/public/QuizCode'
import CategoryCode from './pages/public/CategoryCode'
import OpenQuiz from './pages/public/OpenQuiz'
import ReussirTonConcoursQcm from './pages/public/ReussirTonConcoursQcm'
import Decouvrir from './pages/public/Decouvrir'
import AidePreparationDGFiP from './pages/public/AidePreparationDGFiP'
import AidePreparationATSEM from './pages/public/AidePreparationATSEM'
import Compte from './pages/user/Compte'
import Avancement from './pages/user/Avancement'
import Guide from './pages/user/Guide'
import ScopeLanding from './pages/user/ScopeLanding'
import Quiz from './pages/user/Quiz'
import ResultatQcm from './pages/user/ResultatQcm'
import Abonnement from './pages/user/Abonnement'
import AbonnementRetour from './pages/user/AbonnementRetour'
import FormateurSuivi from './pages/user/FormateurSuivi'
// Admin (/gestion) : chargé à la demande (code-splitting) — les visiteurs
// n'embarquent pas ce code dans le bundle initial.
const AdminLayout = lazy(() => import('./components/layout/AdminLayout'))
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'))
const AdminQuestions = lazy(() => import('./pages/admin/AdminQuestions'))
const AdminContent = lazy(() => import('./pages/admin/AdminContent'))
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'))
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'))
const AdminSeo = lazy(() => import('./pages/admin/AdminSeo'))
const AdminLandings = lazy(() => import('./pages/admin/AdminLandings'))
const AdminCalendrier = lazy(() => import('./pages/admin/AdminCalendrier'))
const AdminJobs = lazy(() => import('./pages/admin/AdminJobs'))
const AdminAiUsage = lazy(() => import('./pages/admin/AdminAiUsage'))
const AdminPackOffers = lazy(() => import('./pages/admin/AdminPackOffers'))
const AdminNotions = lazy(() => import('./pages/admin/AdminNotions'))
const AdminUserContent = lazy(() => import('./pages/admin/AdminUserContent'))
const AdminEmails = lazy(() => import('./pages/admin/AdminEmails'))
const AdminAssets = lazy(() => import('./pages/admin/AdminAssets'))
const AdminUserQuestions = lazy(() => import('./pages/admin/AdminUserQuestions'))
const AdminCategories = lazy(() => import('./pages/admin/AdminCategories'))
const AdminQuickUpdate = lazy(() => import('./pages/admin/AdminQuickUpdate'))
const AdminPartnerSubmissions = lazy(() => import('./pages/admin/AdminPartnerSubmissions'))
const AdminSharePacks = lazy(() => import('./pages/admin/AdminSharePacks'))
const AdminTesters = lazy(() => import('./pages/admin/AdminTesters'))
const AdminFeedback = lazy(() => import('./pages/admin/AdminFeedback'))
const AdminContentRequests = lazy(() => import('./pages/admin/AdminContentRequests'))
const AdminQrcSandbox = lazy(() => import('./pages/admin/AdminQrcSandbox'))
const AdminLandingClaudeHybride = lazy(() => import('./pages/admin/AdminLandingClaudeHybride'))
const AdminLandingHybrideCompare = lazy(() => import('./pages/admin/AdminLandingHybrideCompare'))
const Fonctionnalites = lazy(() => import('./pages/public/Fonctionnalites'))
import RedeemCode from './pages/user/RedeemCode'
import GiftRedeem from './pages/user/GiftRedeem'
import GuidedLearning from './pages/user/GuidedLearning'
import GiftConfirmation from './pages/user/GiftConfirmation'
import PendingGiftPopup from './components/ui/PendingGiftPopup'
import FeedbackButton from './components/feedback/FeedbackButton'
import FeedbackPopup from './components/feedback/FeedbackPopup'

export default function App() {
  const user = useAuthStore(s => s.user);
  const fetchProfile = useAuthStore(s => s.fetchProfile);
  const isPrivileged = user?.role === 'admin' || user?.role === 'reviewer';

  useEffect(() => {
    if (user) fetchProfile();
  }, [!!user]);

  useEffect(() => {
    if (isPrivileged) {
      document.body.classList.remove('protected');
    } else {
      document.body.classList.add('protected');
    }
    return () => document.body.classList.remove('protected');
  }, [isPrivileged]);

  // Micro-animations (cartes au scroll, feedback quiz, glint) — actives pour tous.
  useEffect(() => {
    document.body.classList.add('fx-preview');
    return () => document.body.classList.remove('fx-preview');
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Header />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/connexion" element={<Login />} />
        <Route path="/inscription" element={<Register />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Prototypes privés de la future landing orientée apprentissage (2 hybrides + comparatif) */}
        <Route path="/landingclaudehybride" element={
          <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0', color: 'var(--text-muted)' }}>Chargement du prototype…</div>}>
            <AdminLandingClaudeHybride />
          </Suspense>
        } />
        {/* L'hybride Codex a été promu en page d'accueil */}
        <Route path="/landingcodexhybride" element={<Navigate to="/" replace />} />
        <Route path="/fonctionnalites" element={
          <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0', color: 'var(--text-muted)' }}>Chargement…</div>}>
            <Fonctionnalites />
          </Suspense>
        } />
        <Route path="/landinghybridecompare" element={
          <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0', color: 'var(--text-muted)' }}>Chargement du comparatif…</div>}>
            <AdminLandingHybrideCompare />
          </Suspense>
        } />

        {/* Espace connecte */}
        <Route path="/compte" element={<Compte />} />
        <Route path="/compte/avancement" element={<Avancement />} />
        <Route path="/compte/guide" element={<Guide />} />
        {/* Alias rétro-compat : ancien slug (liens déjà envoyés, onboarding) */}
        <Route path="/compte/bienvenue" element={<Navigate to="/compte/guide" replace />} />
        <Route path="/compte/account/thematiques" element={<Compte />} />
        <Route path="/compte/account/infos" element={<Compte />} />
        <Route path="/compte/account/achats" element={<Compte />} />
        <Route path="/compte/account/parrainage" element={<Compte />} />
        <Route path="/compte/account/faq" element={<Compte />} />
        <Route path="/compte/account/cgu" element={<Compte />} />
        {/* /compte/mes-thematiques → handled by generic :scope route → ScopeLanding hub mode */}
        <Route path="/compte/quiz/:scope" element={<ScopeLanding />} />
        <Route path="/compte/quiz/:scope/quiz/:sousSujetId" element={<Quiz />} />
        <Route path="/compte/quiz/:scope/quiz/:sousSujetId/resultat" element={<ResultatQcm />} />
        <Route path="/compte/quiz/:scope/abonnement" element={<Abonnement />} />
        <Route path="/compte/quiz/:scope/abonnement/retour" element={<AbonnementRetour />} />
        <Route path="/compte/redeem" element={<RedeemCode />} />
        <Route path="/compte/code" element={<GiftRedeem />} />
        <Route path="/compte/code/confirmation" element={<GiftConfirmation />} />
        {/* Alias rétro-compat : liens déjà envoyés (emails cadeau/partage, redirects Stripe) */}
        <Route path="/compte/cadeau" element={<GiftRedeem />} />
        <Route path="/compte/cadeau/confirmation" element={<GiftConfirmation />} />
        <Route path="/compte/quiz/:scope/guide" element={<GuidedLearning />} />

        {/* Admin — route /gestion pour eviter conflit avec dossier physique /admin/ */}
        <Route path="/gestion" element={
          <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0', color: 'var(--text-muted)' }}>Chargement de la gestion…</div>}>
            <AdminLayout />
          </Suspense>
        }>
          <Route index element={<AdminDashboard />} />
          <Route path="questions" element={<AdminQuestions />} />
          <Route path="questions-users" element={<AdminUserQuestions />} />
          <Route path="content" element={<AdminContent />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="seo" element={<AdminSeo />} />
          <Route path="landings" element={<AdminLandings />} />
          <Route path="calendrier" element={<AdminCalendrier />} />
          <Route path="assets" element={<AdminAssets />} />
          <Route path="jobs" element={<AdminJobs />} />
          <Route path="ai-usage" element={<AdminAiUsage />} />
          <Route path="packs" element={<AdminPackOffers />} />
          <Route path="notions" element={<AdminNotions />} />
          <Route path="user-content" element={<AdminUserContent />} />
          <Route path="emails" element={<AdminEmails />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="quick-update" element={<AdminQuickUpdate />} />
          <Route path="partner-submissions" element={<AdminPartnerSubmissions />} />
          <Route path="share-packs" element={<AdminSharePacks />} />
          <Route path="testers" element={<AdminTesters />} />
          <Route path="feedback" element={<AdminFeedback />} />
          <Route path="content-requests" element={<AdminContentRequests />} />
          <Route path="qrc-sandbox" element={<AdminQrcSandbox />} />
        </Route>

        {/* Pages legales */}
        <Route path="/mentions-legales" element={<MentionsLegales />} />
        <Route path="/cgv" element={<CGV />} />
        <Route path="/confidentialite" element={<Confidentialite />} />

        {/* Landing livre KDP "Réussir ton concours à QCM" + page de découverte MonQuiz */}
        <Route path="/reussir-ton-concours-qcm" element={<ReussirTonConcoursQcm />} />
        <Route path="/decouvrir" element={<Decouvrir />} />

        {/* Guide public (acquisition) — le même composant que /compte/guide, mais
            accessible sans connexion (CTA → inscription/connexion pour les visiteurs). */}
        <Route path="/guide" element={<Guide />} />

        {/* Landings d'acquisition SEO par concours */}
        <Route path="/aide-a-la-preparation-concours-dgfip" element={<AidePreparationDGFiP />} />
        <Route path="/aide-a-la-preparation-concours-atsem" element={<AidePreparationATSEM />} />

        {/* Espace partenaire */}
        {/* Ancien tableau de bord partenaire (partage de revenus abandonné) : l'état d'une publication
            est affiché dans la thématique perso (« Gérer les publications »). */}
        <Route path="/partenaire" element={<Navigate to="/compte/quiz/mes-thematiques" replace />} />
        <Route path="/formateur" element={<FormateurSuivi />} />

        {/* Landing thematique personnalisable */}
        <Route path="/ma-thematique" element={<MaThematiqueLanding />} />

        {/* Quiz par code (acces anonyme) */}
        <Route path="/quiz-code" element={<QuizCode />} />
        <Route path="/code" element={<Navigate to="/quiz-code" replace />} />

        {/* Panel de quiz par code categorie (acces anonyme) */}
        <Route path="/category-code" element={<CategoryCode />} />
        <Route path="/open" element={<OpenQuiz />} />
        <Route path="/training" element={<Navigate to="/open" replace />} />

        {/* Acces public (demo sans connexion) */}
        <Route path="/:scope" element={<ScopeLanding />} />
        <Route path="/:scope/quiz/:sousSujetId" element={<Quiz />} />
        <Route path="/:scope/quiz/:sousSujetId/resultat" element={<ResultatQcm />} />
      </Routes>
      {user && <PendingGiftPopup />}
      {user && <FeedbackButton />}
      {user && <FeedbackPopup />}
      <CookieConsent />
      <AdminGateHost />
    </BrowserRouter>
  )
}
